import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Theme, useMediaQuery } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { isMarketingApiError, MarketingApiError } from '~/lib/types/error/MarketingApiError'
import { MarketingButton } from './MarketingButton'
import { theme } from '~/lib/theme'
import { ErrorAlert } from './ErrorAlert'
import { ApolloError } from '@apollo/client'

type SimpleDialogProps = {
  open: boolean
  onOk: () => Promise<void> | void
  onCancel?: () => void
  onClose?: () => void
  title?: string
  titleAlign?: 'left' | 'center' | 'right'
  okButtonText?: string
  closeButtonText?: string
  closeButtonVariant?: 'text' | 'outlined' | 'contained'
  disabledOkButton?: boolean
  disabledCloseButton?: boolean
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg'
  notShrinkContent?: boolean
  mobileFullScreen?: boolean
  zIndex?: number | ((theme: Theme) => number)
}

/**
 * タイトル、OK、キャンセルボタンつきダイアログ
 *
 * OKに非同期のイベントハンドラを渡す場合、{@link MarketingApiError}または{@link ApolloError}をthrowすれば
 * エラー内容を表示します
 */
export const SimpleDialog: React.FC<SimpleDialogProps> = (props) => {
  const {
    open,
    onOk,
    onCancel,
    onClose,
    title,
    titleAlign,
    okButtonText,
    disabledOkButton,
    disabledCloseButton,
    closeButtonText,
    closeButtonVariant,
    children,
    size = 'sm',
    notShrinkContent,
    mobileFullScreen = false,
    zIndex,
  } = props
  const [error, setError] = useState<MarketingApiError | ApolloError>()
  const [isLoading, setIsLoading] = useState(false)
  const errorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setError(undefined)
  }, [open])

  const isBreakpointDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={size}
      onClose={onClose}
      fullScreen={mobileFullScreen && isBreakpointDownSm}
      disableScrollLock={true}
      sx={{
        zIndex,
      }}
    >
      {title && (
        <DialogTitle variant="headingM" sx={{ fontWeight: '500', textAlign: titleAlign ?? 'left' }}>
          {title}
        </DialogTitle>
      )}

      <DialogContent sx={{ flexShrink: notShrinkContent ? 0 : 1 }}>
        <Box ref={errorRef} sx={{ marginBottom: 2 }}>
          {error && <ErrorAlert title="エラーが発生しました" error={error} />}
        </Box>
        {children}
      </DialogContent>

      <DialogActions>
        {onCancel && (
          <MarketingButton
            variant={closeButtonVariant ?? 'text'}
            onClick={async () => {
              onCancel()
            }}
            loading={isLoading}
            disabled={disabledCloseButton}
          >
            {closeButtonText ? closeButtonText : 'キャンセル'}
          </MarketingButton>
        )}
        <MarketingButton
          variant="contained"
          onClick={async () => {
            setIsLoading(true)
            try {
              await onOk()
              setError(undefined)
            } catch (e: unknown) {
              if (isMarketingApiError(e) || e instanceof ApolloError) {
                setError(e)
                errorRef.current?.scrollIntoView({ behavior: 'smooth' })
              }
            } finally {
              setIsLoading(false)
            }
          }}
          loading={isLoading}
          disabled={disabledOkButton}
        >
          {okButtonText ? okButtonText : 'OK'}
        </MarketingButton>
      </DialogActions>
    </Dialog>
  )
}
